import { useEffect, useState } from 'react';

/**
 * Quick 260915-i0x — palette + assignment.
 *
 * The palette is generated, not hand-picked: 20 hues at a uniform HSL
 * lightness of 58 and saturation 72, spaced exactly 18 degrees apart. Uniform
 * lightness is the point — it makes hue the ONLY variable, which is what lets
 * two 20px dots read as different people. `fg` is whichever of near-black /
 * white clears WCAG AA against that background (min ratio in this table is
 * 4.51), which is why the yellow-green band can stay vibrant instead of being
 * dragged dark to survive white text.
 *
 * This replaced a 16-color palette that was really 3 colors: sorted by hue its
 * entries formed three clumps with no neighbour gap above 22 degrees
 * (`#F97316`/`#D2691E` were 0 degrees apart, `#8B5CF6`/`#A78BFA` 3 degrees at
 * equal saturation). Widening 8 -> 16 had added near-duplicates, not colors.
 *
 * Do not "tidy" this into evenly-rounded hex values by hand — the even spacing
 * and the AA guarantee are both asserted in Avatar.test.tsx.
 */
const PALETTE: ReadonlyArray<{ bg: string; fg: string }> = [
  { bg: '#E24B4B', fg: '#171717' },
  { bg: '#E17547', fg: '#171717' },
  { bg: '#E1A347', fg: '#171717' },
  { bg: '#E1D247', fg: '#171717' },
  { bg: '#C2E147', fg: '#171717' },
  { bg: '#94E147', fg: '#171717' },
  { bg: '#66E147', fg: '#171717' },
  { bg: '#47E156', fg: '#171717' },
  { bg: '#47E184', fg: '#171717' },
  { bg: '#47E1B3', fg: '#171717' },
  { bg: '#47E1E1', fg: '#171717' },
  { bg: '#47B3E1', fg: '#171717' },
  { bg: '#4784E1', fg: '#171717' },
  { bg: '#4756E1', fg: '#FFFFFF' },
  { bg: '#6647E1', fg: '#FFFFFF' },
  { bg: '#9447E1', fg: '#FFFFFF' },
  { bg: '#C247E1', fg: '#171717' },
  { bg: '#E147D2', fg: '#171717' },
  { bg: '#E147A3', fg: '#171717' },
  { bg: '#E14775', fg: '#171717' },
];

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

// MKS-203 — the three cached square WebP variants the backend stores
// (backend/src/services/avatarUrl.ts's AVATAR_SIZES). Duplicated here rather
// than imported: this is a frontend-only, zero-import component by design
// (mirrors the palette above), and the three numbers are effectively part of
// the wire contract already encoded in `buildAvatarUrl`'s query string shape.
const AVATAR_SIZES = [64, 128, 256] as const;

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

export interface AvatarProps {
  name: string;
  /**
   * `users.avatar_color_seq` — a monotonic slot assigned in id order, NOT a
   * palette index (the modulo happens here, so the palette can be resized
   * without a backfill).
   *
   * Pass it wherever a user id is in hand. Hashing `name` instead is the
   * birthday problem: measured on a real 43-person team against a 16-color
   * palette, 40 of 43 people collided and one color held 5 of them. Assignment
   * caps a color at ceil(n / 20) people and spreads them evenly.
   *
   * Omit (or pass null) for subjects that genuinely have no slot — external
   * assignees, label-only chips — and the name hash takes over so they still
   * render a stable color.
   */
  colorSeq?: number | null;
  size?: AvatarSize;
  className?: string;
  /**
   * MKS-203 — relative API path to the effective avatar (from
   * `buildAvatarUrl`), or null/undefined when there is none. With this prop
   * absent/empty, or after a previous `onError`, the component renders exactly
   * the initials span below — unchanged from before this prop existed.
   */
  avatarUrl?: string | null;
}

const SIZE_MAP: Record<AvatarSize, string> = {
  xs: 'w-5 h-5 text-[9px]',
  sm: 'w-6 h-6 text-[10px]',
  md: 'w-8 h-8 text-[11px]',
  lg: 'w-10 h-10 text-[13px]',
};

/**
 * Avatar — rounded-full initials over an assigned palette color.
 *
 * Color comes from `colorSeq` when the caller has one, else from a stable hash
 * of `name`. Both paths are deterministic, so a given person renders the same
 * color on every surface. Initials are the first letters of the first two
 * whitespace-separated tokens.
 */
export function Avatar({ name, colorSeq, size = 'md', className = '', avatarUrl }: AvatarProps) {
  // `colorSeq` is a server-assigned integer, but it arrives over the wire:
  // guard against a non-finite or negative value rather than indexing the
  // palette with NaN and rendering a transparent dot. The remainder is made
  // non-negative so a negative slot still lands inside the palette.
  const slot =
    typeof colorSeq === 'number' && Number.isFinite(colorSeq)
      ? ((Math.trunc(colorSeq) % PALETTE.length) + PALETTE.length) % PALETTE.length
      : hash(name) % PALETTE.length;
  // `slot` is in range by construction; the `??` satisfies
  // noUncheckedIndexedAccess without a non-null assertion.
  const { bg, fg } = PALETTE[slot] ?? { bg: '#4784E1', fg: '#171717' };

  const [errored, setErrored] = useState(false);
  // Reset the broken-URL fallback whenever the URL itself changes (e.g. a
  // post-upload repaint with a fresh version token) so a previous failure
  // does not suppress the new photo.
  useEffect(() => {
    setErrored(false);
  }, [avatarUrl]);

  const hasPhoto = typeof avatarUrl === 'string' && avatarUrl.length > 0 && !errored;

  if (hasPhoto) {
    const srcSet = AVATAR_SIZES.map(
      (s, i) => `${avatarUrl}&size=${s} ${i === 0 ? '1x' : i === 1 ? '2x' : '4x'}`,
    ).join(', ');
    return (
      <img
        src={`${avatarUrl}&size=${AVATAR_SIZES[0]}`}
        srcSet={srcSet}
        alt={name}
        loading="lazy"
        decoding="async"
        className={`inline-block rounded-full object-cover ${SIZE_MAP[size]} ${className}`}
        onError={() => setErrored(true)}
      />
    );
  }

  return (
    <span
      className={`inline-flex items-center justify-center rounded-full font-semibold ${SIZE_MAP[size]} ${className}`}
      style={{ background: bg, color: fg }}
      aria-label={name}
    >
      {initials(name)}
    </span>
  );
}
